import React, { useEffect, useMemo, useState } from 'react';
import { Button, Layout, Tooltip } from 'antd';
import {
  CompressOutlined,
  DatabaseOutlined,
  ExpandOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import TeamAssistantChat from './TeamAssistantChat';
import TeamAssistantChatSidebar from './TeamAssistantChatSidebar';
import TeamAssistantKnowledgeDrawer from './TeamAssistantKnowledgeDrawer';
import TeamAssistantHelpModal from './TeamAssistantHelpModal';
import { useTeamAssistantConversationFilters } from '../../hooks/useTeamAssistantQueries';
import { defaultBrowseFilters } from '../../utils/oversightFilters';
import { browseFiltersToApi, isBrowseActive } from '../../utils/teamAssistantBrowse';

const isFullBrowseUpdate = (patch) =>
  patch &&
  typeof patch === 'object' &&
  Object.prototype.hasOwnProperty.call(patch, 'quickPreset') &&
  Object.prototype.hasOwnProperty.call(patch, 'replyStatus') &&
  Object.prototype.hasOwnProperty.call(patch, 'messageRole');

const { Sider, Content } = Layout;

const TEAM_ASSISTANT_SIDEBAR_WIDTH = 260;

const TeamAssistantPageWorkspace = ({ currentUser }) => {
  const [siderCollapsed, setSiderCollapsed] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [browse, setBrowse] = useState(() => defaultBrowseFilters());
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const messagesBrowse = useMemo(
    () => ({
      ...browseFiltersToApi(browse),
      replyStatus: browse.replyStatus ?? 'all',
    }),
    [browse]
  );
  const filtersQuery = useTeamAssistantConversationFilters({ enabled: true });

  const viewingStaffUser = useMemo(() => {
    if (browse.userId == null) return null;
    const match = (filtersQuery.data?.staff || []).find(
      (s) => String(s.user?.id) === String(browse.userId)
    );
    return match?.user ?? { id: browse.userId, name: 'Team member' };
  }, [browse.userId, filtersQuery.data?.staff]);

  const clearStaffFilter = () => {
    setBrowse((prev) => ({ ...prev, userId: undefined }));
  };

  useEffect(() => {
    if (!fullscreen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [fullscreen]);

  const workspaceClass = [
    'team-assistant-workspace',
    fullscreen ? 'team-assistant-workspace--fullscreen' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const headerExtra = (
    <>
      <Tooltip title="What can I do here?">
        <Button
          type="text"
          icon={<QuestionCircleOutlined />}
          onClick={() => setHelpOpen(true)}
          aria-label="Team assistant help"
        />
      </Tooltip>
      <Tooltip title="Knowledge base — uploads, catalog, search, team memory">
        <Button
          type="text"
          icon={<DatabaseOutlined />}
          onClick={() => setKnowledgeOpen(true)}
          aria-label="Open knowledge base"
        />
      </Tooltip>
      <Tooltip title={siderCollapsed ? 'Show filters' : 'Hide filters'}>
        <Button
          type="text"
          icon={siderCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={() => setSiderCollapsed((v) => !v)}
          aria-label="Toggle filters sidebar"
        />
      </Tooltip>
      <Tooltip title={fullscreen ? 'Exit full screen' : 'Full screen'}>
        <Button
          type="text"
          icon={fullscreen ? <CompressOutlined /> : <ExpandOutlined />}
          onClick={() => setFullscreen((v) => !v)}
          aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
        />
      </Tooltip>
    </>
  );

  return (
    <div className={workspaceClass}>
      <TeamAssistantKnowledgeDrawer
        open={knowledgeOpen}
        onClose={() => setKnowledgeOpen(false)}
        currentUser={currentUser}
      />
      <TeamAssistantHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
      <div className="team-assistant-shell">
      <Layout className="team-assistant-workspace-layout">
        <Sider
          className="team-assistant-workspace-sider team-assistant-workspace-sider--gpt"
          width={TEAM_ASSISTANT_SIDEBAR_WIDTH}
          collapsedWidth={0}
          collapsed={siderCollapsed}
          trigger={null}
          theme="light"
        >
          <TeamAssistantChatSidebar
            appearance="chatgpt"
            filters={browse}
            onFiltersChange={(patch) => {
              if (isFullBrowseUpdate(patch)) {
                setBrowse({ ...defaultBrowseFilters(), ...patch });
              } else {
                setBrowse((prev) => ({ ...prev, ...patch }));
              }
            }}
            filterMeta={filtersQuery.data}
            loading={filtersQuery.isLoading}
            collapsed={siderCollapsed}
          />
        </Sider>
        <Content className="team-assistant-workspace-content">
          <TeamAssistantChat
            currentUser={currentUser}
            variant="full"
            title="Team assistant"
            subtitle=""
            browse={messagesBrowse}
            browseActive={isBrowseActive(browse)}
            headerExtra={headerExtra}
            className="team-assistant-root--page team-assistant-root--chatgpt-theme"
            viewingStaffUser={viewingStaffUser}
            onClearStaffFilter={browse.userId != null ? clearStaffFilter : undefined}
            features={{ footnote: false }}
          />
        </Content>
      </Layout>
      </div>
    </div>
  );
};

export default TeamAssistantPageWorkspace;
